import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { route } from '../../../../services/utils';
import { map, switchMap, toArray } from 'rxjs/operators';
import { forkJoin, range } from 'rxjs';
import {
  CROP_FIELD_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
} from '../../../../router/routes';
import Field from '../../../../interfaces/field';
import { useService } from '../../../../services/services';
import Paginate from '../../../../interfaces/paginate';
import Form, { SubmitType } from '../../../../components/form';
import Button from '../../../../components/form/elements/button';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Select, { Option } from '../../../../components/form/elements/select';
import css from './view.module.css';
import Text from '../../../../components/form/elements/text';
import Decimal from '../../../../components/form/elements/decimal';
import Radio from '../../../../components/form/elements/radio-group';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import { useQuery } from '../../../../hooks/use-query';
import TabsKeys from '../../../../enums/tab-keys';
import { Section } from '../../../../pages/crop/fields-and-works';
import Map from '../../../../components/form/elements/map';
import indexCss from '../index.module.css';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import ClassifierGroup from '../../../../enums/classifier-group';
import Classifier from '../../../../enums/classifier';
import ClassifierValue from '../../../../interfaces/classifierValue';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';

export default function MergeFields() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const { fieldService, classifierService } = useService();
  const [formData, setFormData] = useState(null);
  const [params] = useQuery();

  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { mergeData } = useSelector((state: RootState) => ({
    mergeData: state.global.merge,
  }));

  useEffect(() => {
    if (!mergeData) {
      history.goBack();
      return;
    }
  }, [history, mergeData]);

  const schema = yup.object().shape({
    altered_for_current_season: yup
      .boolean()
      .required()
      .test({
        name: 'canMergeForCurrentSeason',
        test: function (value: boolean) {
          const fieldsAlteredWithDeadline =
            this.parent.hasOwnProperty('merged_fields_ids') &&
            this.parent.merged_fields_ids
              .filter(
                (option: Option) =>
                  option.field.altered_for_current_season === true
              )
              .map((option: Option) => {
                return option.label;
              });

          if (!fieldsAlteredWithDeadline) {
            return false;
          }

          if (
            fieldsAlteredWithDeadline &&
            fieldsAlteredWithDeadline.length === 0
          ) {
            return true;
          }

          return value
            ? value
            : this.createError({
                message: ''.concat(
                  t(
                    'Unable to merge fields! Following field(s) have been merged/splitted for current season only:'
                  ),
                  '\n',
                  fieldsAlteredWithDeadline.join('\n')
                ),
              });
        },
      })
      .test({
        name: 'notPastSeasonField',
        exclusive: false,
        params: {},
        message: t(
          'Past season field can be merged/splitted only for current season'
        ),
        test: function (value: boolean) {
          const isPastSeasonField = parseInt(season) < new Date().getFullYear();

          return value === true ? true : !isPastSeasonField;
        },
      }),
    merged_fields_ids: yup
      .array()
      .required()
      .of(
        yup.object().shape({
          value: yup.string().required().uuid(),
        })
      )
      .min(2, 'Must select at least two fields'),
    lad_number: yup.string().max(255).required(),
    name: yup.string().max(255).required(),
    area: yup.number().positive().decimal().required(),
  });
  const transKeyPrefix = 'pages.crop.fields_and_works.fields.merge.';

  const updateNewFieldData = ({ value, setValue }: WatchType) => {
    if (value.length) {
      let ladNumber = value
        .map((v: { field: Field }) => {
          return v.field.lad_number;
        })
        .join('; ');

      let name = value
        .map((v: { field: Field }) => {
          return v.field.name;
        })
        .join('; ');

      setValue('lad_number', ladNumber);
      setValue('name', name);
    }
  };

  const submit = useCallback<SubmitType<Field>>(
    (form: any) => {
      if (Array.isArray(form.merged_fields_ids)) {
        form.merged_fields_ids = form.merged_fields_ids.map(
          (fieldData: any) => {
            return fieldData.value;
          }
        );
      }

      form.crop_group_id = form.crop_group_id?.value;
      form.crop_id = form.crop_id?.value;

      return [
        fieldService.merge(farmId, season, form),
        (response: Field) =>
          history.push(
            route(CROP_FIELD_VIEW, {
              farmId,
              season: season,
              id: response.id,
              tab: TabsKeys.basic_info,
            })
          ),
      ];
    },
    [fieldService, history, farmId, season]
  );

  useEffect(() => {
    if (params['fields']) {
      const fieldIds = params['fields']?.split(',');

      const subscription = range(0, fieldIds.length)
        .pipe(
          map((index) => fieldService.show(farmId, fieldIds[index])),
          toArray(),
          switchMap((array) => forkJoin(array))
        )
        .subscribe((response) => {
          const fields = response
            .map((fieldBase) => {
              return {
                field: fieldBase as Field,
                value: fieldBase.id,
                label: fieldBase.label,
              } as Option;
            })
            .sort((a, b) => (a.field.lad_number > b.field.lad_number ? 1 : -1));

          setFormData(() => {
            return { merged_fields_ids: fields, ...mergeData };
          });
        });
      return () => subscription.unsubscribe();
    } else {
      history.push(
        route(CROP_FIELDS_AND_WORKS_INDEX, {
          farmId: farmId,
          season: season,
          tab: Section.fields,
        })
      );
    }
  }, [farmId, season, params, fieldService, history, mergeData]);

  const [body] = useState(() => [
    Group([
      Radio(
        'altered_for_current_season',
        [
          { value: 'false', label: tp('open-ended') },
          { value: 'true', label: tp('current-season') },
        ],
        tp('deadline')
      ),
      Watch(
        Select('merged_fields_ids', {
          asyncOptions: (value, loadedOptions, { page }) => {
            return fieldService
              .index(farmId, season, { page, title: value, active: 'yes' })
              .pipe(
                map((response: Paginate<Field>) => {
                  return {
                    hasMore: response.total === response.per_page,
                    options: response.data
                      .filter((field) =>
                        mergeData.mergeCandidates.includes(field.id)
                      )
                      .map((field) => ({
                        field: field,
                        label: field.label,
                        value: field.id,
                      })),
                    additional: { page: response.current_page + 1 },
                  };
                })
              );
          },
          multi: true,
          label: transKeyPrefix + 'merged_fields_ids',
          disabled: true,
        }),
        updateNewFieldData
      ),
      Text('lad_number', transKeyPrefix + 'lad_number'),
      Text('name', transKeyPrefix + 'name'),
      Decimal('area', transKeyPrefix + 'area'),
      Watch(
        Select('crop_id', {
          asyncOptions: (value, loadedOptions, { page, getValues }) => {
            const params: any = { page, value };

            const cropGroupId = getValues('crop_group_id')?.value;

            if (cropGroupId) {
              params.crop_group = cropGroupId;
            }

            return classifierService
              .valuesPaged(
                ClassifierGroup.WAREHOUSE,
                Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                params
              )
              .pipe(
                map((response: Paginate<ClassifierValue>) => {
                  return {
                    hasMore: response.last_page > response.current_page,
                    options: response.data.map((v) => ({
                      label: v.value,
                      value: v.id,
                      cropGroup: Object.values(v.properties)
                        .filter(
                          (p) =>
                            p.property.key === ClassifierPropertyKey.CROP_GROUP
                        )
                        .map((p) => {
                          const pv = p.value.pop();
                          return { id: pv.id, label: pv.label };
                        })
                        .pop(),
                    })),
                    additional: { page: response.current_page + 1 },
                  };
                })
              );
          },
          cacheOn: ['crop_group_id'],
          multi: false,
          clearable: true,
          label: transKeyPrefix + 'crop',
        }),
        ({ value, setValue, getValues }) => {
          const cropGroup = getValues('crop_group_id');

          if (value.value) {
            if (value.cropGroup?.id !== cropGroup) {
              setValue('crop_group_id', {
                value: value.cropGroup?.id ?? '',
                label: value.cropGroup?.label ?? '',
              });
            }
          }
        }
      ),
      Text('breed', transKeyPrefix + 'breed'),
    ]),
    Map('default', 'other_fields', undefined, true),
  ]);

  const [footer] = useState(() => [
    Button('merge', null, transKeyPrefix + 'merge'),
    Link(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      }),
      'cancel'
    ),
  ]);

  return (
    <div className={`${indexCss['container']} ${indexCss['show-map']}`}>
      <Form
        title={tp('title')}
        mode="create"
        body={body}
        footer={footer}
        values={formData}
        confirmation={{
          title: tp('confirmation.title'),
          content: (
            <ul className={css['confirmation-list']}>
              <li>{tp('confirmation.l_1')}</li>
              <li>{tp('confirmation.l_2')}</li>
            </ul>
          ),
        }}
        submit={submit}
        schema={schema}
        className={css['field-form']}
      />
    </div>
  );
}
